import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingState, PageHeader, PageShell } from "@/components/app/page";
import { adminAPI } from "@/services/api";
import { formatDate } from "@/utils/constants";
import { getErrorMessage } from "@/utils/errorHelpers";
import { ArrowLeft, GitBranch, Users } from "lucide-react";
import { toast } from "sonner";

export default function AdminPartnerLineagePage() {
  const { partnerId } = useParams();
  const [lineage, setLineage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLineage = async () => {
      setLoading(true);
      try {
        const res = await adminAPI.getPartnerLineage(partnerId);
        setLineage(res.data || null);
      } catch (err) {
        toast.error(getErrorMessage(err, "Erreur chargement lignee"));
        setLineage(null);
      } finally {
        setLoading(false);
      }
    };
    loadLineage();
  }, [partnerId]);

  if (loading) return <LoadingState label="Chargement de la lignee..." />;

  const path = Array.isArray(lineage?.lineage_path) ? lineage.lineage_path : [];
  const descendants = Array.isArray(lineage?.direct_descendants) ? lineage.direct_descendants : [];
  const selected = path.find((item) => item.is_selected);

  return (
    <PageShell data-testid="admin-partner-lineage-page">
      <div className="mb-4">
        <Button asChild variant="outline">
          <Link to="/admin/users">
            <ArrowLeft size={16} className="mr-2" /> Retour base partenaires
          </Link>
        </Button>
      </div>

      <PageHeader
        title="Lignee du partenaire"
        description={selected ? `${selected.full_name || selected.id} occupe la position ${lineage.selected_position}/${lineage.lineage_depth} dans cette lignee.` : "Visualisation administrative de la lignee."}
      />

      {lineage?.cycle_detected && (
        <Card className="mb-6 border-amber-300 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900">
            Une boucle a ete detectee dans cette lignee. Les donnees affichent le parcours exploitable avant la rupture.
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl text-[#2A4B46]">
              <GitBranch size={20} /> Parcours de parrainage
            </CardTitle>
          </CardHeader>
          <CardContent>
            {path.length === 0 ? (
              <p className="py-8 text-center text-sm text-[#5C625F]">Aucune lignee disponible.</p>
            ) : (
              <div className="space-y-4">
                {path.map((partner, index) => (
                  <LineageRow
                    key={`${partner.id}-${index}`}
                    partner={partner}
                    position={index + 1}
                    total={path.length}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-[#E6E4E0] shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-[#2A4B46]">Position</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Metric label="Partenaire" value={selected?.id || partnerId} />
              <Metric label="Position dans la lignee" value={`${lineage?.selected_position || "-"} / ${lineage?.lineage_depth || "-"}`} />
              <Metric label="Parrain direct" value={selected?.parrain_id || "Racine"} />
              <Metric label="Descendants directs" value={lineage?.direct_descendant_count || 0} />
            </CardContent>
          </Card>

          <Card className="border-[#E6E4E0] shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-[#2A4B46]">
                <Users size={18} /> Descendants directs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {descendants.length === 0 ? (
                <p className="py-4 text-sm text-[#5C625F]">Aucun descendant direct.</p>
              ) : (
                <div className="max-h-[480px] space-y-3 overflow-y-auto pr-1">
                  {descendants.map((descendant) => (
                    <div key={descendant.id} className="rounded-xl border border-[#E6E4E0] bg-white p-3">
                      <p className="font-semibold text-[#2A4B46]">{descendant.full_name || "-"}</p>
                      <p className="font-mono text-xs text-[#5C625F]">{descendant.id}</p>
                      <p className="mt-1 text-xs text-[#5C625F]">{descendant.email || "-"}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </PageShell>
  );
}

function LineageRow({ partner, position, total }) {
  return (
    <div className={`rounded-2xl border p-4 ${partner.is_selected ? "border-[#D4A373] bg-[#FFF8ED]" : "border-[#E6E4E0] bg-white"}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2A4B46] text-sm font-bold text-white">{position}</span>
            <p className="font-semibold text-[#2A4B46]">{partner.full_name || "-"}</p>
            {partner.is_selected && <Badge className="bg-[#D4A373] text-white">Partenaire selectionne</Badge>}
          </div>
          <p className="mt-2 font-mono text-xs text-[#5C625F]">{partner.id}</p>
          <p className="break-all text-xs text-[#5C625F]">{partner.email || "-"}</p>
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          <Badge className="bg-[#F3EFE9] text-[#2A4B46]">{partner.partner_level || "P-00"}</Badge>
          <Badge className={partner.is_verified ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}>
            KYC {partner.is_verified ? "validé" : "non validé"}
          </Badge>
        </div>
      </div>
      <div className="mt-4 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Parrain" value={partner.parrain_id || "Racine"} compact />
        <Metric label="PM" value={partner.points_monnaie || 0} compact />
        <Metric label="Ascension" value={partner.points_ascension || 0} compact />
        <Metric label="Inscription" value={formatDate(partner.created_at)} compact />
      </div>
      <p className="mt-3 text-xs text-[#5C625F]">Position {position} sur {total}</p>
    </div>
  );
}

function Metric({ label, value, compact = false }) {
  return (
    <div className={compact ? "rounded-xl bg-[#F9F7F4] p-2" : "rounded-xl bg-[#F9F7F4] p-3"}>
      <p className="text-xs text-[#5C625F]">{label}</p>
      <p className="break-words font-semibold text-[#2A4B46]">{value || "-"}</p>
    </div>
  );
}
